import Extension from "./base/extension";
import Logger from "./utilities/logger";

class Stencil {
  logPrefix = "\x1b[30;45;1m[Stencil]";

  Settings = { debug: false };
  PreInitCallbacks = [];
  PostInitCallbacks = [];

  Extensions = {};
  Helpers = {};

  Cache = new Map();
  Logger = new Logger();

  hasLoaded = false;

  constructor() {
    Object.assign(this, window.Stencil);

    this.hasLoaded = true;

    const urlParams = new URLSearchParams(window.location.search);
    this.Settings.debug = urlParams.get('mpd') || window.theme?.isDevelopment;

    let alertList = document.querySelectorAll('.alert')
    alertList.forEach(function (alert) {
      new bootstrap.Alert(alert);
    });

    // Call onReady method of all extensions once DOMContentLoaded event fired
    document.addEventListener("DOMContentLoaded", () => {
      for (const [key, value] of Object.entries(this.Extensions)) {
        if("onReady" in value){
          try {
            value.onReady(); 
          } catch (e) {
            console.error(e);
          }
        }
      }
    });

    // Make all links with # in #MainContent to scroll to the href ID
    document.querySelectorAll("#MainContent a[href*='#']:not([data-hash-ignore])").forEach(element => {
      element.addEventListener("click", e => {
        document.querySelector(element.href).scrollIntoView({
          behavior: 'smooth'
        });
      });
    });

    // Hide elements specified by data-dismiss attribute, set cookie to keep
    // them hidden for 7 days.
    document.querySelectorAll("[data-dismiss]").forEach(element => {
      if(!Cookies.get(`dismiss-${element.dataset.dismiss}`) || Shopify.designMode){
        document.querySelector(element.dataset.dismiss)?.classList.remove("d-none");
      }
      element.addEventListener("click", e => {
        document.querySelector(element.dataset.dismiss)?.remove();
        Cookies.set(`dismiss-${element.dataset.dismiss}`, true, { expires: 7 });
      });
    });

    // Unhide elements with data attribute 'dismiss' when section loaded in
    // theme customiser.
    if(Shopify.designMode){
      document.addEventListener("shopify:section:load", e => {
        e.target.querySelectorAll("[data-dismiss]").forEach(element => {
          document.querySelector(element.dataset.dismiss)?.classList.remove("d-none");
          console.log("[MP] Unhide element with data-dismiss", element.dataset.dismiss);
        });
      });
    }
  }

  init() {
    // Run all pre-init callbacks registered before script was loaded 
    this.runPreInitCallbacks();

    // Call init method of all extensions straightaway
    for (const [key, value] of Object.entries(this.Extensions)) {
      if("init" in value){
        try {
          value.init();
        } catch (e) {
          console.error(e);
        }
      }
    }

    // Run all post-init callbacks registered before script was loaded 
    this.runPostInitCallbacks();
  }

  /**
   * Register an extension.
   * @param {Extension} extension 
   */
  registerExtension(extension) {
    if(Object.keys(this.Extensions).includes(extension.name)){
      throw new Error(`Extension with name '${extension.name}' already exists`);
    }
    this.Extensions[extension.name] = extension;
  }

  /**
   * Register a helper function.
   * 
   * Extensions can register helpers that other extensions can use.
   * @param {String} name The helper's name
   * @param {Function} func The callback function
   * @param {Object} context The context to run the function in
   */
  registerHelper(name, func, context) {
    if(Object.keys(this.Helpers).includes(name)){
      throw new Error(`Helper with name '${name}' already exists`);
    }
    this.Helpers[name] = func.bind(context);
    const event = new CustomEvent('helper:registered', { detail: { name: name } });
    document.dispatchEvent(event);
  } 

  runPreInitCallbacks() {
    // Run all pre-init callbacks registered before script was loaded 
    while (this.PreInitCallbacks.length){
      try {
        this.PreInitCallbacks.shift().call();
      } catch (e) {
        console.error(this.logPrefix, "Failed to run callback with error", e);
      }
    }
  }

  runPostInitCallbacks() {
    // Run all post-init callbacks registered before script was loaded 
    while (this.PostInitCallbacks.length){
      try {
        this.PostInitCallbacks.shift().call();
      } catch (e) {
        console.error(this.logPrefix, "Failed to run post-init callback with error", e);
      }
    }
  }

  static isProductPage(){ 
    return document.querySelectorAll('.template-product').length > 0;
  }

  static isCollectionPage(){
    return document.querySelectorAll('.template-collection').length > 0;
  }

  static isCartPage(){
    return document.querySelectorAll('.template-cart').length > 0;
  }

  static isBlogPage(){
    return document.querySelectorAll('.template-blog').length > 0;
  }

  static formatMoney(cents, format) {
    if (typeof cents == 'string') { cents = cents.replace('.',''); }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format;
  
    function defaultOption(opt, def) {
       return (typeof opt == 'undefined' ? def : opt);
    }
  
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal   = defaultOption(decimal, '.');
  
      if (isNaN(number) || number == null) { return 0; }
  
      number = (number/100.0).toFixed(precision);
  
      var parts   = number.split('.'),
          dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          cents   = parts[1] ? (decimal + parts[1]) : '';
  
      return dollars + cents;
    }
  
    switch(formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }
  
    return formatString.replace(placeholderRegex, value);
  }

  static handleise(text) {
    return text.toString().toLowerCase()
      .replace(/\s+/g, '-')           // Replace spaces with -
      .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
      .replace(/\-\-+/g, '-')         // Replace multiple - with single -
      .replace(/^-+/, '')             // Trim - from start of text
      .replace(/-+$/, '');            // Trim - from end of text
  }

  static toTitleCase(string){
    return string[0].toUpperCase() + string.slice(1).toLowerCase();
  }

  static getStencilVariable(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(`--📐-${name}`);
  }

  static getAllStencilVariables() {
    const prefix = '--📐-';
    const styles = getComputedStyle(document.documentElement);
    const variables = {};
    console.log('st', styles);
    for (let i = 0; i < styles.length; i++) {
        const name = styles[i];
        if (name.startsWith(prefix)) {
            const key = name.slice(prefix.length); // Remove prefix
            variables[key] = styles.getPropertyValue(name).trim(); // Get and trim value
        }
    }

    return variables;
  }

  /**
   * Given a BS column class string (e.g. col-6 col-lg-3 col-xxl-2),return an 
   * object with the number of items per row at each BS breakpoint.
   * @param {String} columnClass 
   * @returns {Object}
   */
  static getSwiperBreakpointsByBSColClass(columnClass) {
    const itemsPerRow = this.colClassToItemsPerRow(columnClass);
    const bsBreakpoints = this.getBSBreakpoints();
    let breakpoints = { 0: { slidesPerView: itemsPerRow.xs } };
    for (const [key, value] of Object.entries(itemsPerRow)) {
      if (key !== 'xs') {
        breakpoints[bsBreakpoints[key]] = { slidesPerView: value };
      }
    }
    return breakpoints;
  }

  /**
   * Get keyed object of BS breakpoints and their pixel min-widths
   * 
   * @returns {Object} breakpoints
   */
  static getBSBreakpoints() {
    const rootStyles = getComputedStyle(document.documentElement);
    return ['sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'].reduce((breakpoints, bp) => {
      breakpoints[bp] = parseInt(rootStyles.getPropertyValue(`--bs-breakpoint-${bp}`).trim(), 10);
      return breakpoints;
    }, { xs: 0 });
  }

  /**
   * Convert a Bootstrap column class string (e.g. 'col-6 col-lg-4') to an
   * object keyed by breakpoint with the number of items per row.
   * 
   * @param {String} classString 
   * @returns {Object}
   */
  static colClassToItemsPerRow(classString) {
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'];
  
    // Custom class mappings for your custom column classes. These should
    // match the custom width utility in theme.scss.
    const customClasses = {'⅒': 10, '⅛': 8, '⅕': 5, '²⁄₁₅': 7.5, 'auto': 'auto' };
  
    // Default columns in the Bootstrap grid
    const defaultColumns = 12;
  
    // Initialize items per row with defaults - in BS, a column with no class
    // occupies the full row.
    const itemsPerRow = Object.fromEntries(breakpoints.map(bp => [bp, 1]));

    // Split the class string into individual classes
    const classes = classString.trim().split(" ");
    classes.forEach(cls => {
      const parts = cls.split('-');
      const bp = parts.length === 2 ? 'xs' : parts[1];

      // Handle standard Bootstrap classes (col-6, col-md-3, etc.)
      if (cls.startsWith('col-')) {
        const size = parseInt(parts.pop(), 10);
        if (size) {
          itemsPerRow[bp] = defaultColumns / size;
        }
      }
  
      // Handle custom classes (colx-⅒, colx-lg-⅛, etc.)
      if (cls.startsWith('colx-')) {
        const customSize = parts.pop();
        if (customClasses[customSize]) {
          itemsPerRow[bp] = customClasses[customSize];
        }
      }
    });
  
    // Ensure inheritance from smaller breakpoints
    for (let i = 1; i < breakpoints.length; i++) {
      const prevBp = breakpoints[i - 1];
      const currentBp = breakpoints[i];
  
      if (!classes.some(cls => cls.includes(`-${currentBp}`))) {
        itemsPerRow[currentBp] = itemsPerRow[prevBp];
      }
    }

    return itemsPerRow;
  }

  /**
   * Convert a rem value to pixels
   * 
   * @param {String|Number} rem 
   * @returns {Number}
   */
  static remToPx(rem) {
    rem = parseFloat(rem); 
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
  }

  /**
   * Show a Bootstrap Alert
   * 
   * @param {String} type 
   * @param {String} content 
   * @param {String} icon 
   */
  static alert(type = "primary", content = "<strong>This</strong> is an alert", icon = "", closeAfter = 5000) {
    let alert = document.querySelector("#alert-container > .alert");
    alert.className = `${alert.className.replace(/\balert-.+/g, '')} alert-${type}`;
    let alertContent = alert.querySelector(".alert-message");
    content = `<div>${content}</div>`;
    if(icon){
      content = `<i class="fs-4 me-2 ${icon}"></i>${content}`;
    }
    alertContent.innerHTML = content;
    alert.classList.add("show");
    setTimeout(() => {
      alert.classList.remove("show")
    }, closeAfter);
  }

  /**
   * Show a Bootstrap Toast
   * 
   * @param {String} type 
   * @param {String} content 
   * @param {String} icon 
   */
  static toast(type = "success", content = "<strong>This</strong> is a toast!", icon = "") {
    // Get the template and clone it
    let container = document.querySelector("#toasts");
    let template = document.querySelector('#toast');
    let clone = template.content.firstElementChild.cloneNode(true);

    // Set the alert class, message and optional icon
    let wrapper = clone.querySelector("div:first-child");
    wrapper.className = `${wrapper.className} p-0 m-0 alert alert-${type}`;
    clone.querySelector(".toast-message").innerHTML = content;
    if(icon){
      clone.querySelector(".toast-icon").innerHTML = `<i class="fs-4 me-2 ${icon}"></i>`;
    }

    // Add to the DOM, and show
    container.appendChild(clone);
    let toast = new bootstrap.Toast(clone);
    toast.show();
  }

  static fetchConfig(type = "json") {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
    };
  }
}

export default Stencil;